/**
 * List Compare Service
 * Implements business logic for list comparison operations
 * Supports difference, intersection, and union operations
 */

import { EncodingOperation } from '../types';
import { ProcessingError, ValidationError } from '../utils/errorHandler';
import { CommonValidator } from '../validation/commonValidation';

export type CompareMode = 'difference' | 'intersection' | 'union';

export interface CompareResult {
  mode: CompareMode;
  leftOnly: string[];
  rightOnly: string[];
  common: string[];
  union: string[];
}

export class ListCompareService {
  /**
   * Compare two lists
   */
  static async compare(
    leftList: string,
    rightList: string,
    mode: CompareMode = 'difference'
  ): Promise<EncodingOperation> {
    try {
      // Validate inputs
      const leftValidation = CommonValidator.validateText(leftList,
        CommonValidator.getValidationRules('list-compare' as any)
      );
      const rightValidation = CommonValidator.validateText(rightList,
        CommonValidator.getValidationRules('list-compare' as any)
      );

      if (!leftValidation.isValid) {
        throw new ValidationError(leftValidation.error!, 'list-compare' as any, leftValidation.suggestion);
      }
      if (!rightValidation.isValid) {
        throw new ValidationError(rightValidation.error!, 'list-compare' as any, rightValidation.suggestion);
      }

      // Parse lists
      const leftItems = this.parseList(leftList);
      const rightItems = this.parseList(rightList);

      // Perform comparison
      const result = this.performComparison(leftItems, rightItems, mode);

      return {
        id: this.generateOperationId(),
        toolType: 'list-compare' as any,
        operationType: 'compare',
        inputData: `${leftList}\n---SEPARATOR---\n${rightList}`,
        outputData: result,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          mode,
          leftCount: leftItems.length,
          rightCount: rightItems.length,
          leftOnlyCount: result.leftOnly.length,
          rightOnlyCount: result.rightOnly.length,
          commonCount: result.common.length,
          unionCount: result.union.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to compare lists',
        'list-compare' as any,
        'Please check your input and try again'
      );
    }
  }

  /**
   * Parse list from text (one item per line)
   */
  private static parseList(text: string): string[] {
    if (!text.trim()) return [];
    return text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
  }

  /**
   * Perform list comparison
   */
  private static performComparison(
    leftItems: string[],
    rightItems: string[],
    mode: CompareMode
  ): CompareResult {
    const leftSet = new Set(leftItems);
    const rightSet = new Set(rightItems);

    const leftOnly = leftItems.filter(item => !rightSet.has(item));
    const rightOnly = rightItems.filter(item => !leftSet.has(item));
    const common = leftItems.filter(item => rightSet.has(item));
    const union = [...new Set([...leftItems, ...rightItems])];

    return {
      mode,
      leftOnly,
      rightOnly,
      common,
      union
    };
  }

  /**
   * Format result for display
   */
  static formatResult(result: CompareResult): string {
    const lines: string[] = [];

    switch (result.mode) {
      case 'difference':
        lines.push(`=== Items only in Left (${result.leftOnly.length}) ===`);
        lines.push(...result.leftOnly);
        lines.push('');
        lines.push(`=== Items only in Right (${result.rightOnly.length}) ===`);
        lines.push(...result.rightOnly);
        break;

      case 'intersection':
        lines.push(`=== Common Items (${result.common.length}) ===`);
        lines.push(...result.common);
        break;

      case 'union':
        lines.push(`=== All Unique Items (${result.union.length}) ===`);
        lines.push(...result.union);
        break;
    }

    return lines.join('\n');
  }

  /**
   * Generate unique operation ID
   */
  private static generateOperationId(): string {
    return `list-compare-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get compare mode info for display
   */
  static getCompareModeInfo(mode: CompareMode): { name: string; description: string } {
    switch (mode) {
      case 'difference':
        return {
          name: 'Difference',
          description: 'Show items unique to each list (A-B, B-A)'
        };
      case 'intersection':
        return {
          name: 'Intersection',
          description: 'Show items common to both lists (A∩B)'
        };
      case 'union':
        return {
          name: 'Union',
          description: 'Show all unique items from both lists (A∪B)'
        };
      default:
        return {
          name: 'Difference',
          description: 'Show items unique to each list'
        };
    }
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}
